"use client";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';

const items = [
    {
        title: 'Audience Strategy',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="12" cy="11" r="5" stroke="currentColor" strokeWidth="2" />
                <path d="M4 27c0-6 3.5-10 8-10s8 4 8 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="23" cy="13" r="4" stroke="currentColor" strokeWidth="2" />
                <path d="M18.5 27c.5-4.5 3-8 5.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'Identify audiences based on customer behaviour, interests, lifecycle stage and business value — not demographics alone.',
    },
    {
        title: 'Creative Strategy',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'Develop hooks, messaging, formats and concepts designed to capture attention quickly and communicate relevance.',
    },
    {
        title: 'Lead Generation',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="7" width="24" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M4 9l12 9 12-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'Build campaigns designed to generate enquiries while maintaining focus on lead quality and downstream conversion.',
    },
    {
        title: 'Ecommerce Advertising',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <path d="M6 10h20l-2 14H8L6 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="M11 10V8a5 5 0 0 1 10 0v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'Connect product discovery, creative, retargeting and conversion optimisation across the customer journey.',
    },
    {
        title: 'Retargeting',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <path d="M6 16a10 10 0 1 1 3 7.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M6 23v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'Re-engage people who have interacted with your website, content, products or brand with messaging relevant to their stage in the journey.',
    },
    {
        title: 'Creative Testing',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="6" width="10" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
                <rect x="18" y="6" width="10" height="20" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M9 12v4M23 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'Test different hooks, formats, messages, offers and creative directions to understand what actually changes performance.',
    },
];

const CARD_W = 320;
const GAP = 20;

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
                d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
                stroke={INK}
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const AUTO_MS = 2000;

export default function MetaAdsWhatWeManage() {
    const scrollRef = useRef<HTMLDivElement>(null);
    const activeRef = useRef(0);
    const [active, setActive] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        activeRef.current = active;
    }, [active]);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const children = Array.from(el.children) as HTMLElement[];
        let closest = 0;
        let minDist = Infinity;
        children.forEach((child, idx) => {
            const dist = Math.abs(child.offsetLeft - el.scrollLeft);
            if (dist < minDist) {
                minDist = dist;
                closest = idx;
            }
        });
        setActive(closest);
    }, []);

    const scrollToIndex = (i: number) => {
        const el = scrollRef.current;
        if (!el) return;
        const card = el.children[i] as HTMLElement | undefined;
        if (!card) return;
        el.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
        // update immediately rather than waiting on a native `scroll` event, which
        // doesn't reliably fire alongside programmatic smooth-scroll + scroll-snap
        activeRef.current = i;
        setActive(i);
    };

    const nudge = (dir: -1 | 1) => scrollToIndex((activeRef.current + dir + items.length) % items.length);

    useEffect(() => {
        if (isPaused) return;
        const id = setInterval(() => {
            const next = (activeRef.current + 1) % items.length;
            scrollToIndex(next);
        }, AUTO_MS);
        return () => clearInterval(id);
    }, [isPaused]);

    return (
        <section
            className="relative overflow-hidden pt-14 pb-20 md:pt-20 md:pb-28"
            style={{ background: '#ffffff', borderRadius: '80px' }}
        >
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.035]"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />

            {/* decorative background washes */}
            <div
                className="absolute pointer-events-none"
                style={{
                    top: '-12%', right: '-6%', width: 560, height: 480,
                    background: 'radial-gradient(circle, rgba(55,48,163,0.1) 0%, transparent 70%)',
                    borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                }}
            />

            <div className="max-w-[1200px] mx-auto flex flex-col items-center text-center gap-6 relative px-6 md:px-16 lg:px-24">
                <div className="flex flex-col items-center gap-4">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        What We Cover
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        What We Manage
                    </motion.h2>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-sm font-mono hidden md:inline" style={{ color: 'rgba(8,4,0,0.35)', letterSpacing: '0.05em' }}>
                        auto-plays, hover to pause
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => nudge(-1)}
                            aria-label="Previous"
                            className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity duration-200"
                            style={{ border: '1px solid rgba(8,4,0,0.15)', background: '#fff' }}
                        >
                            <ChevronIcon dir="left" />
                        </button>
                        <button
                            onClick={() => nudge(1)}
                            aria-label="Next"
                            className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity duration-200"
                            style={{ border: '1px solid rgba(8,4,0,0.15)', background: '#fff' }}
                        >
                            <ChevronIcon dir="right" />
                        </button>
                    </div>
                </div>
            </div>

            {/* carousel */}
            <div className="relative mt-2">
                <div
                    className="absolute inset-y-0 left-0 w-10 md:w-24 z-10 pointer-events-none"
                    style={{ background: 'linear-gradient(to right, #ffffff, transparent)' }}
                />
                <div
                    className="absolute inset-y-0 right-0 w-10 md:w-24 z-10 pointer-events-none"
                    style={{ background: 'linear-gradient(to left, #ffffff, transparent)' }}
                />

                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onTouchStart={() => setIsPaused(true)}
                    className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 px-6 md:px-16 lg:px-24"
                    style={{ scrollbarWidth: 'none' }}
                >
                    {items.map((item, i) => (
                        <motion.div
                            key={item.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                            className="relative shrink-0 snap-start rounded-[26px] p-7 flex flex-col gap-5"
                            style={{ width: CARD_W, background: '#fbfdfd', border: '1px solid rgba(8,4,0,0.09)', boxShadow: '0 2px 0 rgba(8,4,0,0.03)' }}
                        >
                            <div className="flex items-start justify-between">
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: 'rgba(55,48,163,0.07)', border: '1px solid rgba(55,48,163,0.16)', color: PURPLE }}
                                >
                                    {item.icon}
                                </div>
                                <span
                                    className="text-[10px] font-bold px-2 py-1 rounded-full"
                                    style={{ background: INK, color: LIME, fontFamily: CLASH, letterSpacing: '0.04em' }}
                                >
                                    AD {String(i + 1).padStart(2, '0')}
                                </span>
                            </div>

                            <div className="flex flex-col gap-2">
                                <h3 className="text-lg font-bold leading-snug" style={{ fontFamily: CLASH, color: INK, letterSpacing: '0.01em' }}>
                                    {item.title}
                                </h3>
                                <p className="text-sm leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)' }}>
                                    {item.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* dots */}
            <div className="flex items-center justify-center gap-2 mt-8 relative z-10">
                {items.map((item, i) => (
                    <button
                        key={item.title}
                        onClick={() => scrollToIndex(i)}
                        aria-label={`Go to ${item.title}`}
                        className="rounded-full transition-all duration-300"
                        style={{
                            width: active === i ? 22 : 7,
                            height: 7,
                            background: active === i ? PURPLE : 'rgba(8,4,0,0.15)',
                        }}
                    />
                ))}
            </div>
        </section>
    );
}
